import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Rocket, CheckCircle2, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "./publicUI";
import {
  IMG, ABOUT_EMIRA, ABOUT_B2B, WHY_US_STATS, VISION, MISSION, CORE_VALUES, GEN_EMIRA, TEAM, COMPANY,
} from "./siteContent";

const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function About() {
  const nav = useNavigate();
  return (
    <div>
      <section className="relative brand-gradient-navy overflow-hidden">
        <div className="absolute inset-0 brand-dotgrid opacity-50" />
        <img src={IMG.aboutMeeting} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-300 mb-3">About Us</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] max-w-3xl">
            Connecting global businesses with job-ready talent
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">{COMPANY.positioning}</p>
        </div>
      </section>

      {/* ABOUT EMIRA */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Eyebrow>About Emira</Eyebrow>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">A leading overseas recruitment &amp; training agency</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">{ABOUT_EMIRA}</p>
          <p className="text-slate-600 mt-4 leading-relaxed">{ABOUT_B2B}</p>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-xl"><img src={IMG.workforce} alt="Professional workforce" className="w-full h-[440px] object-cover" /></div>
      </section>

      {/* WHY CHOOSE US STATS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>Why Choose Us</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Committed to quality, speed and reliability</h2></div>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {WHY_US_STATS.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-slate-200 p-7 hover-lift">
                <div className="font-display text-xl font-bold text-[#0088FF]">{s.title}</div>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-6">
        {[{ icon: Target, t: "Vision", d: VISION }, { icon: Rocket, t: "Mission", d: MISSION }].map((c) => (
          <div key={c.t} className="brand-gradient-navy rounded-3xl p-8 relative overflow-hidden">
            <div className="absolute inset-0 brand-dotgrid opacity-30" />
            <div className="relative">
              <div className="h-12 w-12 rounded-xl bg-[#0088FF] grid place-items-center text-white mb-4"><c.icon className="h-6 w-6" /></div>
              <h3 className="font-display text-2xl font-bold text-white">{c.t}</h3>
              <p className="text-slate-300 mt-3 leading-relaxed">{c.d}</p>
            </div>
          </div>
        ))}
      </section>

      {/* CORE VALUES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>What We Stand For</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Core Values</h2></div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CORE_VALUES.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover-lift">
                <div className="font-display text-4xl font-extrabold text-[#0088FF]/20">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display font-semibold text-lg text-[#0A192F] mt-1">{v.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GEN EMIRA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-12 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <Eyebrow>GenEmira</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">Our online tech ecosystem</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">{GEN_EMIRA}</p>
          </div>
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg"><img src={IMG.gulf} alt="Global connectivity" className="w-full h-64 object-cover" /></div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl"><Eyebrow>Our Team</Eyebrow>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#0A192F] mt-2">The people behind Emira</h2></div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="team-grid">
            {TEAM.map((m, i) => (
              <motion.div key={m.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 text-center hover-lift">
                <div className="h-16 w-16 rounded-full bg-[#0A192F] text-white grid place-items-center mx-auto font-display font-bold text-lg">{initials(m.name)}</div>
                <div className="font-display font-semibold text-[#0A192F] mt-3">{m.name}</div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{m.title}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="brand-gradient-navy rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute inset-0 brand-dotgrid opacity-40" />
          <div className="relative flex flex-col lg:flex-row gap-6 lg:items-center lg:justify-between">
            <div className="flex items-start gap-4 max-w-2xl">
              <Quote className="h-10 w-10 text-[#0088FF] shrink-0" />
              <h2 className="font-display text-2xl lg:text-3xl font-bold text-white">Partner with a team that treats your project like its own.</h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" data-testid="about-cta-quote" onClick={() => nav("/for-employers")} className="bg-[#0088FF] hover:bg-[#0070E0] h-12">Request a Quote</Button>
              <Button size="lg" variant="outline" data-testid="about-cta-contact" onClick={() => nav("/contact")} className="h-12 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white">Contact Us</Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
